/**
 * Notes Service
 * Handles Firestore operations for user notes
 * Path: /users/{uid}/notes/{noteId}
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { logger } from '@/lib/logger';
import { db } from '@/lib/firebase';
import { Note, NoteInput, NoteQueryOptions, NoteChecklistItem } from '@/types/notepad';
import { createTask } from './tasks.service';

/**
 * Get notes collection reference for a user
 */
function getNotesCollection(userId: string) {
  return collection(db, 'users', userId, 'notes');
}

/**
 * Convert Firestore document to Note
 */
function docToNote(docSnap: DocumentSnapshot<DocumentData>): Note {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ownerId: data.ownerId,
    teamId: data.teamId,
    title: data.title,
    content: data.content || '',
    checklist: data.checklist || [],
    tags: data.tags || [],
    pinned: data.pinned || false,
    archived: data.archived || false,
    visibility: 'private',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    deletedAt: data.deletedAt,
  };
}

/**
 * Generate unique ID for checklist items
 */
function generateChecklistItemId(): string {
  return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * List notes with optional filters
 */
export async function listNotes(userId: string, options: NoteQueryOptions = {}): Promise<Note[]> {
  try {
    const notesRef = getNotesCollection(userId);
    const constraints: QueryConstraint[] = [];

    // Exclude soft-deleted notes
    constraints.push(where('deletedAt', '==', null));

    // Filter by archived status
    if (options.archived !== undefined) {
      constraints.push(where('archived', '==', options.archived));
    }

    // Filter by team context
    if (options.teamId) {
      constraints.push(where('teamId', '==', options.teamId));
    }

    // Order by pinned first, then updatedAt
    if (options.pinnedFirst) {
      constraints.push(orderBy('pinned', 'desc'));
    }
    constraints.push(orderBy('updatedAt', 'desc'));

    // Apply limit
    if (options.limit) {
      constraints.push(firestoreLimit(options.limit));
    }

    const q = query(notesRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(docToNote);
  } catch (error) {
    logger.error('[NotesService] Failed to list notes:', error);
    throw error;
  }
}

/**
 * Get a single note by ID
 */
export async function getNote(userId: string, noteId: string): Promise<Note | null> {
  try {
    const noteRef = doc(db, 'users', userId, 'notes', noteId);
    const noteSnap = await getDoc(noteRef);

    if (!noteSnap.exists()) {
      return null;
    }

    const note = docToNote(noteSnap);

    // Don't return soft-deleted notes
    if (note.deletedAt) {
      return null;
    }

    return note;
  } catch (error) {
    logger.error('[NotesService] Failed to get note:', error);
    throw error;
  }
}

/**
 * Create a new note
 */
export async function createNote(userId: string, input: NoteInput): Promise<Note> {
  try {
    const notesRef = getNotesCollection(userId);

    // Process checklist items to add IDs
    const checklist: NoteChecklistItem[] = input.checklist
      ? input.checklist.map((item) => ({
          id: generateChecklistItemId(),
          text: item.text,
          done: item.done || false,
          doneAt: item.done ? Timestamp.now() : null,
        }))
      : [];

    const noteData = {
      ownerId: userId,
      teamId: input.teamId || null,
      title: input.title,
      content: input.content || '',
      checklist,
      tags: input.tags || [],
      pinned: input.pinned || false,
      archived: input.archived || false,
      visibility: 'private' as const,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deletedAt: null,
    };

    const docRef = await addDoc(notesRef, noteData);
    const newNote = await getNote(userId, docRef.id);

    if (!newNote) {
      throw new Error('Failed to retrieve created note');
    }

    return newNote;
  } catch (error) {
    logger.error('[NotesService] Failed to create note:', error);
    throw error;
  }
}

/**
 * Update an existing note
 */
export async function updateNote(
  userId: string,
  noteId: string,
  input: Partial<NoteInput>
): Promise<Note> {
  try {
    const noteRef = doc(db, 'users', userId, 'notes', noteId);

    const updates: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    };

    if (input.title !== undefined) updates.title = input.title;
    if (input.content !== undefined) updates.content = input.content;
    if (input.tags !== undefined) updates.tags = input.tags;
    if (input.pinned !== undefined) updates.pinned = input.pinned;
    if (input.archived !== undefined) updates.archived = input.archived;
    if (input.teamId !== undefined) updates.teamId = input.teamId;

    // Process checklist if provided
    if (input.checklist !== undefined) {
      const checklist: NoteChecklistItem[] = input.checklist.map((item) => ({
        id: generateChecklistItemId(),
        text: item.text,
        done: item.done || false,
        doneAt: item.done ? Timestamp.now() : null,
      }));
      updates.checklist = checklist;
    }

    await updateDoc(noteRef, updates);

    const updatedNote = await getNote(userId, noteId);
    if (!updatedNote) {
      throw new Error('Failed to retrieve updated note');
    }

    return updatedNote;
  } catch (error) {
    logger.error('[NotesService] Failed to update note:', error);
    throw error;
  }
}

/**
 * Update note checklist
 */
export async function updateNoteChecklist(
  userId: string,
  noteId: string,
  checklist: NoteChecklistItem[]
): Promise<Note> {
  try {
    const noteRef = doc(db, 'users', userId, 'notes', noteId);

    await updateDoc(noteRef, {
      checklist,
      updatedAt: serverTimestamp(),
    });

    const updatedNote = await getNote(userId, noteId);
    if (!updatedNote) {
      throw new Error('Failed to retrieve updated note');
    }

    return updatedNote;
  } catch (error) {
    logger.error('[NotesService] Failed to update checklist:', error);
    throw error;
  }
}

/**
 * Pin or unpin a note
 */
export async function toggleNotePin(userId: string, noteId: string): Promise<Note> {
  try {
    const note = await getNote(userId, noteId);
    if (!note) {
      throw new Error('Note not found');
    }

    const noteRef = doc(db, 'users', userId, 'notes', noteId);
    await updateDoc(noteRef, {
      pinned: !note.pinned,
      updatedAt: serverTimestamp(),
    });

    const updatedNote = await getNote(userId, noteId);
    if (!updatedNote) {
      throw new Error('Failed to retrieve updated note');
    }

    return updatedNote;
  } catch (error) {
    logger.error('[NotesService] Failed to toggle pin:', error);
    throw error;
  }
}

/**
 * Archive or unarchive a note
 */
export async function toggleNoteArchive(userId: string, noteId: string): Promise<Note> {
  try {
    const note = await getNote(userId, noteId);
    if (!note) {
      throw new Error('Note not found');
    }

    const noteRef = doc(db, 'users', userId, 'notes', noteId);
    await updateDoc(noteRef, {
      archived: !note.archived,
      updatedAt: serverTimestamp(),
    });

    const updatedNote = await getNote(userId, noteId);
    if (!updatedNote) {
      throw new Error('Failed to retrieve updated note');
    }

    return updatedNote;
  } catch (error) {
    logger.error('[NotesService] Failed to toggle archive:', error);
    throw error;
  }
}

/**
 * Soft delete a note
 */
export async function deleteNote(userId: string, noteId: string): Promise<void> {
  try {
    const noteRef = doc(db, 'users', userId, 'notes', noteId);
    await updateDoc(noteRef, {
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    logger.error('[NotesService] Failed to delete note:', error);
    throw error;
  }
}

/**
 * Restore a soft-deleted note
 */
export async function restoreNote(userId: string, noteId: string): Promise<Note> {
  try {
    const noteRef = doc(db, 'users', userId, 'notes', noteId);
    await updateDoc(noteRef, {
      deletedAt: null,
      updatedAt: serverTimestamp(),
    });

    const updatedNote = await getNote(userId, noteId);
    if (!updatedNote) {
      throw new Error('Note not found after restore');
    }
    return updatedNote;
  } catch (error) {
    logger.error('[NotesService] Failed to restore note:', error);
    throw error;
  }
}

/**
 * Permanently delete a note (hard delete)
 */
export async function permanentlyDeleteNote(userId: string, noteId: string): Promise<void> {
  try {
    const noteRef = doc(db, 'users', userId, 'notes', noteId);
    await deleteDoc(noteRef);
  } catch (error) {
    logger.error('[NotesService] Failed to permanently delete note:', error);
    throw error;
  }
}

/**
 * Search notes by text content
 */
export async function searchNotes(
  userId: string,
  searchText: string,
  options: NoteQueryOptions = {}
): Promise<Note[]> {
  try {
    // Get all notes (Firestore doesn't support full-text search natively)
    const notes = await listNotes(userId, options);

    // Filter by search text (case-insensitive)
    const searchLower = searchText.toLowerCase();
    return notes.filter(
      (note) =>
        note.title.toLowerCase().includes(searchLower) ||
        note.content.toLowerCase().includes(searchLower) ||
        note.tags?.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  } catch (error) {
    logger.error('[NotesService] Failed to search notes:', error);
    throw error;
  }
}

/**
 * Promote a checklist item to a standalone task
 * Creates a Task and backfills the taskId on the checklist item
 */
export async function promoteChecklistItemToTask(
  userId: string,
  noteId: string,
  itemId: string
): Promise<{ note: Note; taskId: string }> {
  try {
    // Get the note
    const note = await getNote(userId, noteId);
    if (!note) {
      throw new Error('Note not found');
    }

    // Find the checklist item
    const item = note.checklist?.find((i) => i.id === itemId);
    if (!item) {
      throw new Error('Checklist item not found');
    }

    // Create a task from the checklist item
    const task = await createTask(
      userId,
      {
        title: item.text,
        noteId: noteId,
        status: item.done ? 'done' : 'open',
        teamId: note.teamId,
      },
      {
        mapperOptions: {
          createdVia: 'note_promotion',
          createdViaRefId: noteId,
        },
      }
    );

    // Update the checklist item with the taskId
    const updatedChecklist = note.checklist?.map((i) =>
      i.id === itemId ? { ...i, taskId: task.id } : i
    );

    // Update the note
    const updatedNote = await updateNoteChecklist(userId, noteId, updatedChecklist || []);

    return {
      note: updatedNote,
      taskId: task.id,
    };
  } catch (error) {
    logger.error('[NotesService] Failed to promote checklist item:', error);
    throw error;
  }
}
